import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Platform } from '@ionic/angular';

// ionic
import { Events } from '../helpers/events';

// rxjs
import { take } from 'rxjs/operators';
import { ReplaySubject } from "rxjs";
import { Observable } from 'rxjs';

// other
import * as Sentry from '@sentry/browser';
import * as moment from "moment";

// services
import { OverlayService } from "./overlay.service";


// models
import { ApiResponse } from "../models/api-response";
import { User } from "../models/user";
import { Event } from "../models/event";
import { Participant } from "../models/participant";
import { Blacklist } from "../models/blacklist";

// config
import { environment } from "../../environments/environment";
import { theme } from "../../environments/theme";

@Injectable({
    providedIn: 'root'
})
export class UserService {

    /**
     * current user
     *
     * @type {ReplaySubject}
     */
    private currentUser: any = new ReplaySubject<User>(1);

    /**
      * constructor
      *
      * @param jwtHttp
      * @param events
      * @param eventProvider
      * @param overlayService
      */
    constructor(
        private http: HttpClient,
        private router: Router,
        public events: Events,
        //public eventProvider: EventProvider,
        public overlayService: OverlayService,
        public platform: Platform
    ) {
        this.events.subscribe('user:current', ((data) => this.loadCurrentUser(true, null, data && data.eventId ? data.eventId : null)));
        this.events.subscribe('user:current:participant', ((data) => this.loadCurrentUser(true, data && !!data.redirectLink ? data.redirectLink : null, data && data.eventId ? data.eventId : null)));
        // this.events.subscribe('loggedin', (() => this.loadCurrentUser()));
        this.events.subscribe('loggedout', (() => this.clearCurrentUser()));
    }

    /**
     * get current user
     *
     * @return {Observable<User>}
     */
    public getCurrentUser(): Observable<User> {
        return this.currentUser.asObservable();
    }

    /**
     * set current user
     *
     * @param user
     */
    public setCurrentUser(user: User) {
        if (user.selected_participant_id && user.selected_participant.event) {
            user.selected_participant.event = new Event(user.selected_participant.event);
            //this.eventProvider.setCurrentEvent(new Event(user.selected_participant.event, user));
        }
        this.currentUser.next(user);

        if (user.selected_participant) {
            this.events.publish('custom:design', {
                setting: user.selected_participant.event.setting,
            });

            let showWizard = user.selected_participant.event.show_wizard;

            if (user.selected_participant.event.use_groups) {
                let group = user.selected_participant.event.groupSettings.filter(group => group.id == user.selected_participant.group_id);
                if (group[0] && group[0].show_wizard) {
                    showWizard = true;
                } else {
                    showWizard = false;
                }
            }

            let step = 0;
            // show wizard only after accepting TOC
            if (!user.toc_needed && !user.toc_refresh && user.selected_participant instanceof Participant) {

                // check how many steps is available in event
                let wizardSteps = 3;;
                if (!user.selected_participant.event.matching_status) {
                    wizardSteps = 2;
                }

                // check if any item is required, or wizard is enabled and user has not filled it yet
                if ((step = user.selected_participant.showWizard())
                    || (showWizard && (!user.selected_participant.wizard_status || user.selected_participant.wizard_status < wizardSteps))) {

                    // console.info('Step: ' + step);
                    // console.info('showWizard: ' + showWizard);

                    setTimeout(() => {
                        this.events.publish('custom:profile', {
                            profile_setting: user.selected_participant.event_setting.profile_setting,
                            step: step
                        });
                    }, 500);
                }
            }
        }

        // set participant ID for error loging
        if (environment.sentry.enabled) {
            Sentry.configureScope(scope => {
                //scope.setTag('user_mode', 'admin');
                scope.setUser({ id: "" + user.selected_participant_id });
            });
        }
    }

    /**
     * reload current user
     *
     * @return void
     */
    public loadCurrentUser(withParticipant: boolean = true, redirectLink = null, eventId = null) {

        let headers = new HttpHeaders();
        let params = new HttpParams();
        params = params.append("withParticipant", withParticipant ? "true" : "false");
        if (eventId) {
            params = params.append("event_id", eventId);
        }

        this.http.get(environment.api + '/user/current', { headers: headers, params: params })
            .subscribe((user: User) => {
                this.setCurrentUser(new User(user));
                if (redirectLink) {
                    this.router.navigate([redirectLink], { replaceUrl: true });
                }
            }, error => {
                if (!error.error || !error.status) {
                    //this.overlayService.showConnectionProblems(error);
                    this.events.publish('logout');
                }
            });
    }

    /**
     * clear current User
     *
     * @return void
     */
    public clearCurrentUser() {
        //this.currentUser = new ReplaySubject<User>(1);
        this.setCurrentUser(new User());
    }

    /**
     * update user password
     *
     * @param user
     *
     * @return {Observable<any>}
     */
    public updatePassword(user: User): Observable<any> {
        return this.http.put(environment.api + '/user/password/' + user.id, user);
    }

    /**
     * destroy user by id
     *
     * @param id
     *
     * @return {Observable<any>}
     */
    public delete(id: number): Observable<any> {
        return this.http.delete(environment.api + '/user/' + id);
    }

    /**
     * unset users linkedin     *
     *
     * @return {Observable<any>}
     */
    public unsetLinkedIn(): Observable<any> {
        return this.http.post(environment.api + '/user/unset-linkedin', {});
    }

    /**
     * set selected user event
     *
     * @param userId
     * @param eventId
     *
     * @return {Observable<any>}
     */
    public setSelectedEvent(userId: number, eventId: number): Observable<any> {
        const params = { event_id: eventId };

        return this.http.put(environment.api + '/user/selected-event/' + userId, JSON.stringify(params));
    }

    /**
     * use event key
     *
     * @param userId
     * @param eventKey
     *
     * @return {Observable<any>}
     */
    public useEventKey(eventKey: string): Observable<any> {
        const params = {
            event_key: eventKey
        };

        return this.http.post(environment.api + '/user/event-key', JSON.stringify(params));
    }

    /**
     * update device
     *
     * @param data
     *
     * @returns {Observable<any>}
     */
    public updateDevice(data: any): Observable<any> {
        return this.http.post(environment.api + '/user/update-device', JSON.stringify({
            device: data,
            app: theme.name,
        }));
    }

    /**
     * update device
     *
     * @param data
     *
     */
    public checkLiveStatus(liveStatus): void {
        if (liveStatus['user_updated_at']) {
            this.getCurrentUser().pipe(
                take(1)
            ).subscribe((user) => {
                if (user.id) {
                    let userUpdateAt = moment(liveStatus.user_updated_at, 'YYYY-MM-DD HH:mm:ss').toDate();
                    if (user && user.date_updated_at < userUpdateAt) {
                        this.loadCurrentUser()
                    }
                }
            })

        }
    }

    /**
     * set TOC
     *
     * @param any
     *
     * @return {Observable<any>}
     */
    public setTOC(model: any): Observable<any> {
        return this.http.post(environment.api + '/user/toc', model);
    }

    /**
 * get live data for user
 * @return void
 */
    public getLiveState(id: number): Observable<any> {
        return this.http.get(environment.api + '/user/' + id + '/live-state');
    }

    /**
     * attach participant
     *
     * @param string
     *
     * @return {Observable<any>}
     */
    public attachParticipant(invitation_token: string): Observable<any> {
        return this.http.post(environment.api + '/user/attach-participant', { 'invitation_token': invitation_token });
    }

    /**
     * set security cookies for cloudfront
     *
     * @param string
     *
     * @return {Observable<any>}
     */
    public setCloudfrontCookies(): Observable<any> {
        // set cookies for assets cloudfront distribution
        if (!environment.cookieForAssets) {
            return this.http.get(environment.api + '/user/cookies/refresh?ngsw-bypass=true');
        } else {
            if (environment.sameSiteAssets && !this.platform.is('cordova')) {
                // NOTE[jg] sync this with plt.apiPrefix
                return this.http.get(location.origin + '/api/v2/user/cookies/refresh?ngsw-bypass=true&sameSiteAssets=true');
            } else {
                return this.http.get(environment.assets + '/api/v2/user/cookies/refresh?ngsw-bypass=true');
            }
        }
    }

    /**
     * unset security cookies for cloudfront
     *
     * @param string
     *
     * @return {Observable<any>}
     */
    public unsetCloudfrontCookies(): Observable<any> {

        // const headerDict = {
        //     'Authorization': 'Bearer xxx',
        // };

        // const requestOptions = new RequestOptions();
        // requestOptions.headers = new Headers(headerDict);
        // requestOptions.withCredentials = true;

        let headers = new HttpHeaders();
        let params = new HttpParams();

        headers = headers.append("Authorization", 'Bearer xxx');

        // set cookies for assets cloudfront distribution
        if (environment.name == 'local') {
            return this.http.get(environment.api + '/user/cookies/remove?ngsw-bypass=true', { headers: headers, params: params });
        } else {
            return this.http.get(environment.assets + '/api/v2/user/cookies/remove?ngsw-bypass=true', { headers: headers, params: params });
        }
    }

    /**
     * update users blacklist
     *
     * @param {Blacklist} blacklist
     *
     * @return {Observable<any>}
     */
    public updateBlacklist(blacklist: Blacklist): Observable<any> {
        return this.http.post(environment.api + '/user/blacklist', blacklist);
    }

    /**
     * check user video upload status
     *
     * @param {string} id
     *
     * @return {Observable<any>}
     */
    public checkVideoUpload(id: string): Observable<any> {
        return this.http.get(environment.api + '/user/check-video-upload/' + id);
    }
}
